import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraFormField, SpecraInlineBanner, SpecraOverlayDialog, SpecraTextarea } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useState } from 'react';
import { PursuitTargetCombobox } from './PursuitTargetCombobox';
import { SurgeryComponentPicker, selectedComponents } from './SurgeryComponentPicker';
import { ReviewAck, SurgeryError, SurgeryFooterActions } from './SurgeryDialogParts';
import { selectionNeedsReview, useMovePursuitComponents, usePursuitSurgeryPlan } from './surgeryHooks';
/**
 * GRW-19: "Move items to another pursuit…" with modes Move / Link / Detach.
 * A detach-only selection needs no target.
 */ export function MovePursuitComponentsDialog({ captureProjectId, open, onOpenChange }) {
    return /*#__PURE__*/ _jsx(SpecraOverlayDialog, {
        open: open,
        onOpenChange: onOpenChange,
        title: "Move items to another pursuit",
        size: "lg",
        scrollBody: true,
        testId: Testids.pursuitSurgery.moveDialog,
        children: open && /*#__PURE__*/ _jsx(MoveBody, {
            captureProjectId: captureProjectId,
            onDone: ()=>onOpenChange(false)
        })
    });
}
function MoveBody({ captureProjectId, onDone }) {
    const [requestId] = useState(()=>crypto.randomUUID());
    const plan = usePursuitSurgeryPlan(captureProjectId, true);
    const move = useMovePursuitComponents();
    const [target, setTarget] = useState('');
    const [reason, setReason] = useState('');
    const [selection, setSelection] = useState({});
    const [ack, setAck] = useState(false);
    const components = selectedComponents(plan.data?.components ?? [], selection);
    const detachOnly = components.length > 0 && components.every((c)=>c.mode === 'detach');
    const needsReview = selectionNeedsReview(components) && (plan.data?.reviewRequiredIfMovingRfis ?? false);
    const canSubmit = components.length > 0 && (detachOnly || !!target && target !== captureProjectId) && !!reason.trim() && (!needsReview || ack) && !move.isPending;
    const submit = ()=>move.mutate({
            fromCaptureProjectId: captureProjectId,
            toCaptureProjectId: detachOnly ? '' : target,
            components,
            reason: reason.trim(),
            requestId,
            proposalReviewAck: ack
        }, {
            onSuccess: onDone
        });
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-4",
        children: [
            /*#__PURE__*/ _jsx(SpecraFormField, {
                label: "Move to",
                required: !detachOnly,
                children: /*#__PURE__*/ _jsx(PursuitTargetCombobox, {
                    excludeId: captureProjectId,
                    value: target,
                    onChange: (id)=>setTarget(id)
                })
            }),
            plan.isError ? /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                tone: "danger",
                variant: "framed",
                children: "The parts of this pursuit could not be loaded."
            }) : /*#__PURE__*/ _jsx(SurgeryComponentPicker, {
                components: plan.data?.components ?? [],
                selection: selection,
                onChange: setSelection,
                allowDetach: true
            }),
            needsReview && /*#__PURE__*/ _jsx(ReviewAck, {
                proposals: plan.data?.impactedProposals ?? [],
                verb: "move",
                checked: ack,
                onCheckedChange: setAck
            }),
            /*#__PURE__*/ _jsx(SpecraFormField, {
                label: "Reason",
                htmlFor: "move-reason",
                required: true,
                children: /*#__PURE__*/ _jsx(SpecraTextarea, {
                    id: "move-reason",
                    value: reason,
                    onChange: (e)=>setReason(e.target.value),
                    "data-testid": Testids.pursuitSurgery.reasonInput
                })
            }),
            /*#__PURE__*/ _jsx(SurgeryError, {
                error: move.error
            }),
            /*#__PURE__*/ _jsx(SurgeryFooterActions, {
                onCancel: onDone,
                onSubmit: submit,
                disabled: !canSubmit,
                pending: move.isPending,
                label: "Move"
            })
        ]
    });
}
